import { Button, ButtonVariant, type DefaultProps } from '@egw/components'
import { type FeatureFlags, WEALTH_ROUTES } from '@egw/constants'
import { useFlags } from '@egw/feature-flags'
import { useTranslation } from '@egw/i18n'
import { useRouter } from '@egw/router'
import cx from 'classnames'
import { useMemo, type FC } from 'react'
import { twMerge } from 'tailwind-merge'

type InvestmentsOverviewOverflowMenuProps = DefaultProps

export const InvestmentsOverviewOverflowMenu: FC<InvestmentsOverviewOverflowMenuProps> = ({
  className,
  id,
  testId,
  ariaLabel,
}) => {
  const { t } = useTranslation('features-investment')

  const { flags } = useFlags<FeatureFlags>()

  const { navigate } = useRouter()
  // Component base class names. Can be overriden by className property.
  const baseClass = cx('overflow-menu flex flex-col items-start')

  // Final class names
  const finalClasses = twMerge(baseClass, className)

  const INVESTMENT_OVERFLOW_OPTIONS = [
    {
      title: t('account_options.statements'),
      onClick: () => {
        navigate(WEALTH_ROUTES.DOCUMENTS_STATEMENTS.path)
      },
      key: WEALTH_ROUTES.DOCUMENTS_STATEMENTS.path,
    },
    {
      title: t('account_options.tax_documents'),
      onClick: () => {
        navigate(WEALTH_ROUTES.DOCUMENTS.path)
      },
      key: `${WEALTH_ROUTES.DOCUMENTS.path}-tax-documents`,
    },
    {
      title: t('account_options.trade_confirmations'),
      onClick: () => {
        navigate(WEALTH_ROUTES.DOCUMENTS.path)
      },
      key: `${WEALTH_ROUTES.DOCUMENTS.path}-trade-confirmations`,
    },
    {
      title: t('account_options.account_beneficiaries'),
      onClick: () => {
        navigate(WEALTH_ROUTES.SETTINGS_BENEFICIARIES.path)
      },
      key: WEALTH_ROUTES.SETTINGS_BENEFICIARIES.path,
    },
  ]

  const addAccountTitle = useMemo(() => {
    return flags.rolloutJointEnrollmentV1 ? t('account_options.add_account_v2') : t('account_options.add_account')
  }, [flags.rolloutJointEnrollmentV1, t])

  return (
    <div
      className={finalClasses}
      {...(testId ? { 'data-testid': testId } : {})}
      {...(id ? { id } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
    >
      {INVESTMENT_OVERFLOW_OPTIONS.map((option, index) => (
        <button
          data-testid={`investment-overview-overflow-menu-option-${index}`}
          key={option.key}
          className="w-full text-text-body typo-primary-base-regular flex py-sm px-2xs border-b border-light last-of-type:border-none"
          type="button"
          onClick={option.onClick}
        >
          {option.title}
        </button>
      ))}
      {flags.personalPortfolioMulltiAccountEnrollment ? (
        <Button
          data-testid="overflow-menu-add-account"
          type="button"
          onClick={() => {
            navigate(WEALTH_ROUTES.ENROLLMENT.path)
          }}
          variant={ButtonVariant.Secondary}
          className="mt-sm w-full"
        >
          {addAccountTitle}
        </Button>
      ) : null}
    </div>
  )
}
